import { useQueryGetAllBots } from "@apis:bot/bot";
import { useQueryGetAllOrganizations } from "@apis:organization/organization";
import { useBranding } from "@app-setup:BrandingContext";
import { SearchableSelect } from "@components:SearchableSelect/SearchableSelect";
import { SVGInternalIcon } from "@components:SVGInternalIcon";
import { useBotSelection } from "@hooks:useBotSelection";
import { useNewChat } from "@hooks:useNewChat";
import { useSearchActions } from "@stores:app/talk/search";
import { size } from "lodash";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { AppType } from "@:constants";

interface BotSelectionProps {
  value?: string | null; // botId
  onChange?: (botId: string) => void;
  noMatchText?: string;
  app?: AppType;
  hideOnSingleBot?: boolean;
  autoSelectBot?: boolean;
}

export const BotSelection = (props: BotSelectionProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const searchActions = useSearchActions();
  const { newChat } = useNewChat();

  const isSetBotByAuto = useRef<boolean>(false);

  const isChatPage = pathname.startsWith("/app/chat");
  const isSearchPage = pathname.startsWith("/app/search");

  const listAllBotsQuery = useQueryGetAllBots(true, props.app);
  const listAllOrganizations = useQueryGetAllOrganizations();

  const combinedBotAndOrganizations = useMemo(() => {
    return listAllBotsQuery.data?.map((bot) => {
      const organization = listAllOrganizations.data?.find(
        (org) => org.id === bot.organization_id,
      );
      return {
        ...bot,
        organizationName: organization?.name,
      };
    });
  }, [listAllBotsQuery?.data, listAllOrganizations?.data]);

  const canGroupByOrganization = useMemo(() => {
    if (!listAllBotsQuery.data) return false;

    const uniqueOrganizationIds = new Set(
      listAllBotsQuery.data.map((bot) => bot.organization_id),
    );

    return uniqueOrganizationIds.size >= 1;
  }, [listAllBotsQuery.data]);

  const current = combinedBotAndOrganizations?.find(
    (bot) => bot.id === props.value,
  );

  const sortedBots = useMemo(() => {
    return (
      combinedBotAndOrganizations?.sort((a, b) => {
        return (
          a.organizationName?.localeCompare(b.organizationName as string) ?? 0
        );
      }) ?? []
    );
  }, [combinedBotAndOrganizations]);

  const isLoading =
    listAllBotsQuery?.isLoading || listAllOrganizations?.isLoading;

  useEffect(() => {
    if (isSetBotByAuto.current) return;
    if (props.autoSelectBot) {
      const firstBot = sortedBots[0];
      if (firstBot && !current && props.value !== firstBot.id && !isLoading) {
        props.onChange?.(firstBot.id);
        isSetBotByAuto.current = true;
      }
    }
  }, [
    props.autoSelectBot,
    props.value,
    props.onChange,
    sortedBots,
    current,
    isLoading,
  ]);

  if (props.hideOnSingleBot && !isLoading && size(listAllBotsQuery.data) <= 1) {
    return null;
  }

  return (
    <SearchableSelect
      options={sortedBots}
      getOptionLabel={(option) => option?.name as string}
      getOptionValue={(option) => option?.id as string}
      selectedOption={current}
      groupBy={
        canGroupByOrganization
          ? (option) => option?.organizationName as string
          : undefined
      }
      SearchProps={{
        placeholder: canGroupByOrganization
          ? t("botSelection.botName")
          : t("botSelection.botOrCustomerName"),
      }}
      RenderValueProps={{
        startAdornment: (
          <SVGInternalIcon icon="botIcon" sx={{ width: 22, height: 22 }} />
        ),
      }}
      onChange={async (botId) => {
        if (isChatPage) {
          newChat();
        }

        if (isSearchPage) {
          searchActions.reset();
        }

        props?.onChange?.(botId);
      }}
      isLoading={isLoading}
      loadingText={t("botSelection.loadingBots")}
      filterOptions={(option, searchText) => {
        return (
          option?.name?.toLowerCase()?.includes(searchText.toLowerCase()) ||
          option?.organizationName
            ?.toLowerCase()
            ?.includes(searchText.toLowerCase()) ||
          false
        );
      }}
      noMatchText={props?.noMatchText}
    />
  );
};

interface BotSelectionPersistentProps {
  app?: AppType;
}

export const BotSelectionPersistent = ({
  app,
}: BotSelectionPersistentProps) => {
  const { t } = useTranslation();
  const { setSelectedBotId, selectedBotId } = useBotSelection();
  const { setCurrentBotId, isAllBotsSameOrg } = useBranding();

  // Sync selected bot with branding context
  useEffect(() => {
    setCurrentBotId(selectedBotId ?? null);
  }, [selectedBotId, setCurrentBotId]);

  useEffect(() => {
    return () => {
      if (isAllBotsSameOrg) return;
      setCurrentBotId(null);
    };
  }, [setCurrentBotId, isAllBotsSameOrg]);

  return (
    <BotSelection
      value={selectedBotId}
      onChange={setSelectedBotId}
      noMatchText={t("botSelection.noBotsAvailable")}
      app={app}
      hideOnSingleBot={!!app}
      autoSelectBot={!!app}
    />
  );
};
